import { apiRequest } from '../api';

// ── Settings › Posting accounts (batch 11 R4) ─────────────────────────────────────────────────────────────────────────
// The shapes server/src/routes/settleAccounts.ts answers, field for field (the W1 brief §4.7). A GET carries ?clientId=;
// a write carries `clientId` in its body — the route's gate reads it there. The whole mount is refused to a client's
// own books login (403, middleware.ts), and the screen is never offered to one (menus.ts staffOnly).
// ⛔ The server decides which accounts may answer a role (`eligible`), whether a stored answer still holds (`problem`)
// and what "+ New account" may take (`cfSections`); the screen only draws it. The three lists mirrored here are the ones
// the screen words by code (a role's name, a rule's sentence, a refusal's sentence), and scripts/testSettleI18n.ts
// asserts each equals the server's own export (services/settleAccounts.ts), in order.

/** services/settleAccounts.ts SETTLE_ROLES, in the order the screen lists them. */
export const SA_ROLES = ['wht_payable', 'wht_prepaid'] as const;
export type SaRole = typeof SA_ROLES[number];
/** services/settleAccounts.ts SETTLE_ACCOUNT_RULES — why an account may not answer, in the order the server tests. */
export const SA_ACCOUNT_RULES = ['not_found', 'other_client', 'deleted', 'inactive', 'non_postable', 'control', 'cash', 'class'] as const;
export type SaRule = typeof SA_ACCOUNT_RULES[number];
/** services/settleAccounts.ts SETTLE_ERRORS — every refusal the route answers with. */
export const SA_ERRORS = ['invalid', 'invalid_role', 'no_chart', 'account_refused', 'account_invalid', 'code_taken'] as const;

export type SaAccount = { id: string; code: string; name_th: string; name_en: string };
export type SaProblem = { rule: SaRule | string; message: string } | null;
export type SaRoleRow = {
  role: SaRole; classes: string[]; accountId: string; account: SaAccount | null; problem: SaProblem;
  setBy: string; setByName: string; setAt: number; eligible: SaAccount[];
};
export type SaMap = { hasChart: boolean; rows: SaRoleRow[]; answered: number; total: number; codesInUse: string[]; cfSections: string[] };
export type SaLogRow = {
  id: number; at: number; role: string; oldAccountId: string; newAccountId: string;
  oldAccount: SaAccount | null; newAccount: SaAccount | null; actorName: string; source: string;
};
export type SaLogPage = { rows: SaLogRow[]; total: number; page: number; limit: number };
export type SaNewAccount = { code: string; nameTh: string; nameEn?: string; cfSection: string };

const q = (clientId: string, extra: Record<string, string | number | undefined> = {}) => {
  const p = new URLSearchParams({ clientId });
  for (const [k, v] of Object.entries(extra)) if (v !== undefined && v !== '') p.set(k, String(v));
  return p.toString();
};

export const settleAccountsApi = {
  /** The two roles, their answers checked again now, what each may be answered with, and the new-account sections. */
  get: (clientId: string): Promise<SaMap> => apiRequest('GET', `/settle-accounts?${q(clientId)}`),
  /** accountId '' clears the answer (the row stays, account_id ''). A save that changes nothing writes nothing. */
  put: (clientId: string, role: SaRole, accountId: string): Promise<{ changed: boolean }> =>
    apiRequest('PUT', `/settle-accounts/${encodeURIComponent(role)}`, { clientId, accountId }),
  /** One chart row through accountingSetup.addAccount and the answer it was made for, in one transaction. 201. */
  create: (clientId: string, role: SaRole, a: SaNewAccount): Promise<{ accountId: string }> =>
    apiRequest('POST', `/settle-accounts/${encodeURIComponent(role)}/new`, { clientId, ...a }),
  /** The answers given, newest first, 100 a page. */
  log: (clientId: string, page = 1): Promise<SaLogPage> => apiRequest('GET', `/settle-accounts/log?${q(clientId, { page })}`),
};
